import React, { useEffect, useMemo, useState } from 'react';
import set from 'lodash/set';
import { Helmet } from 'react-helmet-async';
import { makeStyles } from '@mui/styles';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import BackIcon from '@mui/icons-material/ChevronLeft';
import Button from 'components/Button';
import Spin from 'components/Spin';
import Wrapper from 'components/PageWrapper';
import WidgetsPreview from '../components/connect-widget/WidgetsPreview';
import ConnectLinkItem from '../components/connect-widget/ConnectLinkItem';
import Design from '../components/connect-widget/Design';
import WidgetOptions from '../components/connect-widget/WidgetOptions';
import {
  deleteConnectCardFromCollection,
  getCardsForConnectPoint,
  getConnectCards,
  getConnectPoints,
  saveConnectCardToCollection,
  saveConnectPoint,
} from 'services/connect-card.service';
import { snack } from '../../../utils/toast';
import { useSelector } from 'react-redux';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import MuiLink from '@mui/material/Link';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/AddCircle';
import SortableConnectCards from '../components/connect-hub/SortableConnectCards';
import { arrayMove } from '@dnd-kit/sortable';
import TextInlineEdit from 'components/TextInlineEdit';
import useAlert from 'hooks/useAlert';
import Picker from 'components/Picker';
import useRequest from 'hooks/useRequest';

const pathMap = {
  0: 'design',
  1: 'cards',
  2: 'widgets',
};

const useStyles = makeStyles((theme) => ({
  root: {
    paddingBottom: 0,
  },
  aside: {
    width: 340,
    display: 'flex',
    flexDirection: 'column',
    // height: 'calc(100vh - 140px)',
  },
  rightPanel: {
    minHeight: 'calc(100vh - 160px)',
    // maxHeight: 600,
    // height: '100%',
  },
  card: {
    'padding': theme.spacing(2),
    'border': '1px solid',
    'borderColor': theme.palette.divider,
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
  iconButton: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 4,
    marginLeft: 8,
    padding: 8,
  },
}));

const defaultWidgetSettings = {
  chat: {
    enabled: false,
    text: 'Join Us',
    subtitle: '',
    position: 'right',
  },
  banner: {
    enabled: false,
    desc: '',
    position: 'top',
    buttonText: 'OK!',
  },
  popup: {
    isTimed: false,
    enabled: false,
    title: '',
    desc: '',
    videoUrl: '',
    buttonText: 'OK!',
    waitDays: '',
  },
  stickyButton: {
    enabled: false,
    title: 'people have recently planned...',
    buttonText: 'Plan your visit now',
    position: 'left',
    socialProof: true,
  },
};

const steps = [
  'Design Your Widget',
  'Select Your Connect Cards',
  'Configure Your Widget',
];

const ConnectWidgetEditor = ({ history, location, match: { params } }) => {
  const id = params.id || 'create';
  const isNew = id === 'create';
  const classes = useStyles();
  const alert = useAlert();
  const account = useSelector((s) => s.settings.account);
  const [data, setData] = useState({
    collection_name: '',
    collection_title: '',
    collection_description: '',
    collection_template: '',
    logo_or_video: '',
    logo_or_video_url: '',
    primary_color: '#' + (account.account_color || '2186eb'),
    collection_text_color: '#000000',
    collection_settings: { ...defaultWidgetSettings },
  });
  const [collectionCards, { loading: loadingCards, updateCache, refetch }] =
    useRequest(getCardsForConnectPoint, id, {
      skip: isNew,
    });
  const [connectCards] = useRequest(getConnectCards, {
    account_id: account.account_id,
  });
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed, setCompleted] = React.useState({});

  const pickerOptions = connectCards
    ? connectCards.map((c) => ({
        label: c.card_name,
        value: c.hash,
        id: c.id,
      }))
    : [];

  const selectedOptions = pickerOptions.filter((o) => {
    const existing = collectionCards?.find((c) => c.card?.hash === o.value);
    if (existing) {
      o.hash = existing.hash;
      return true;
    }
  });

  useEffect(() => {
    if (!params.stepId) {
      history.replace(`${location.pathname}/design`);
    }
    if (!isNew) {
      setLoading(true);

      getConnectPoints({ hash: id, account_id: account.account_id }).then(
        (res) => {
          const collection = res[0];
          if (!collection.collection_settings) {
            collection.collection_settings = { ...defaultWidgetSettings };
          }
          setData({
            ...data,
            ...collection,
            collection_text_color:
              collection.collection_text_color || '#000000',
          });
          setLoading(false);
        },
      );
    }
  }, [id]);

  useEffect(() => {
    history.push(`/connect/widgets/${id}/${pathMap[activeStep]}`);
  }, [activeStep]);

  const totalSteps = () => {
    return steps.length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all steps have been completed,
          // find the first step that has been completed
          steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const onInputChange = (e) => {
    const { name, value } = e.target;
    set(data, name, value);
    setData({ ...data });
  };

  const onRadioChange = (e) => {
    const { name, checked, value } = e.target;
    console.log(name, checked, checked);
    set(data, name, checked);
    setData({ ...data });
  };

  const collectName = () => {
    return new Promise((resolve, reject) => {
      alert.prompt({
        severity: 'info',
        placeholder: 'i.e. First Time Guests',
        title: 'Give your Connect Widget a name',
        description:
          'Before saving your Connect Widget, give it a name so you can find it easier later.',
        confirmationText: 'Next',
        onConfirm: resolve,
        onCancel: () =>
          reject(new Error('Please give your Connect Widget a name')),
      });
    });
  };

  const onNext = async (value) => {
    try {
      if (!data.collection_name) {
        data.collection_name = await collectName();
      }
      const resp = await saveConnectPoint(data);
      if (!data.hash) {
        history.replace(`/connect/widgets/${resp.hash}/${value}`);
      } else {
        history.push(`/connect/widgets/${id}/${value}`);
      }
      handleNext();
    } catch (e) {
      snack.error(e.message);
    }
  };

  const onToggleCard = async (card) => {
    const inCollection = collectionCards.find((c) =>
      c.redirect_url
        ? c.redirect_url === card.redirect_url
        : c.connect_card_id === card.id,
    );
    if (inCollection) {
      updateCache((prev) => prev.filter((c) => c !== inCollection));
      if (inCollection.hash) {
        await deleteConnectCardFromCollection(inCollection.hash);
      }
    } else {
      const resp = await saveConnectCardToCollection({
        collection_id: data.id,
        connect_card_id: card.id,
        overlay_text: card.overlay_text,
        redirect_url: card.redirect_url,
        sort_order: collectionCards.length,
      });
      refetch();
    }
  };

  const onSave = async () => {
    try {
      await saveConnectPoint(data);
      snack.success('Connect point saved successfully');
    } catch (e) {
      snack.error(e.message);
    }
  };

  const onSaveClose = async () => {
    try {
      await onSave();
      history.push('/connect/widgets');
    } catch (e) {
      snack.error(e.message);
    }
  };

  const onRemoveCard = async (card) => {
    await deleteConnectCardFromCollection(card.hash);
    updateCache((prev) => prev.filter((c) => c !== card));
  };

  const onSort = async (cards, oldIndex, newIndex) => {
    const oldCard = cards[oldIndex];
    const newCard = cards[newIndex];
    updateCache((prev) => arrayMove(prev, oldIndex, newIndex));
    await Promise.all([
      saveConnectCardToCollection({
        hash: oldCard.hash,
        sort_order: newIndex,
      }),
      saveConnectCardToCollection({
        hash: newCard.hash,
        sort_order: oldIndex,
      }),
    ]);
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const onPickerSelect = async (cards) => {
    const added = cards.filter((c) => !selectedOptions.includes(c));
    const removed = selectedOptions.filter((c) => !cards.includes(c));

    await deleteMany(removed.map((c) => c.hash));
    await addMany(added);
    await refetch();
  };

  const onDelete = async (card) => {
    await deleteConnectCardFromCollection(card.hash);
    updateCache((prev) => prev.filter((c) => c.hash !== card.hash));
  };

  const deleteMany = async (hashes) => {
    await Promise.all(hashes.map((h) => deleteConnectCardFromCollection(h)));
  };

  const addMany = async (cards) => {
    return Promise.all(
      cards.map((i, idx) => {
        const card = connectCards.find((c) => c.hash === i.value);
        return saveConnectCardToCollection({
          collection_id: data.id,
          connect_card_id: card.id,
          overlay_text: card.overlay_text,
          redirect_url: card.redirect_url,
          sort_order: collectionCards.length + idx,
        });
      }),
    );
  };

  const onUpdateConnectCard = async (values) => {
    await saveConnectCardToCollection(values);
    return refetch();
  };

  // const cardsForCollection = useMemo(() => {
  //   console.log(collectionCards);
  //   return collectionCards.map((cc) => {
  //     const card = connectCards.find((i) => i.id === cc.connect_card_id);
  //     cc.card = card;
  //     return cc;
  //   });
  // }, [connectCards, collectionCards]);

  return (
    <Wrapper
      className={classes.root}
      title={
        <>
          <Breadcrumbs>
            <MuiLink
              to="/connect/widgets"
              component={Link}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <BackIcon sx={{ mr: 0.5 }} color="disabled" /> Connect Widgets
            </MuiLink>
            <Typography>{isNew ? 'Create New' : 'Edit'}</Typography>
          </Breadcrumbs>
        </>
      }
    >
      <Helmet title="Connect Widget" />
      <Spin spinning={loading}>
        <Grid container spacing={2}>
          <Grid item>
            <Box className={classes.aside}>
              <Box mb={2}>
                <TextInlineEdit
                  text={data.collection_name}
                  label={
                    <Typography variant="h2">
                      {data.collection_name || 'Untitled Widget'}
                    </Typography>
                  }
                  onSave={(v) =>
                    onInputChange({
                      target: { name: 'collection_name', value: v },
                    })
                  }
                />
              </Box>
              <Paper sx={{ flex: 1 }}>
                <Box py={2} px={1}>
                  <Stepper nonLinear activeStep={activeStep}>
                    {steps.map((label, index) => (
                      <Step key={label} completed={completed[index]}>
                        <StepButton
                          disabled={isNew}
                          color="inherit"
                          onClick={handleStep(index)}
                        >
                          {activeStep === index ? label : null}
                        </StepButton>
                      </Step>
                    ))}
                  </Stepper>
                </Box>
                <Divider />
                <Box p={2}>
                  {activeStep === 0 && (
                    <Design
                      data={data}
                      onInputChange={onInputChange}
                      onRadioChange={onRadioChange}
                      onStepChange={onNext}
                    />
                  )}
                  {activeStep === 1 && (
                    <>
                      <SortableConnectCards
                        onSort={onSort}
                        cards={collectionCards ?? []}
                        onStepChange={onNext}
                        onUpdateConnectCard={onUpdateConnectCard}
                        onDelete={onDelete}
                      />
                      <Picker
                        placeholder="Search Connect Cards"
                        onChange={onPickerSelect}
                        value={selectedOptions}
                        options={pickerOptions}
                      >
                        <Button startIcon={<AddIcon />} variant="text">
                          Add a Connect Card
                        </Button>
                      </Picker>
                      <Box mb={3} mt={2}>
                        <Typography gutterBottom fontWeight={500}>
                          Create A Simple Link Card
                        </Typography>
                        <ConnectLinkItem
                          onClick={onToggleCard}
                          data={{ title: 'New Card' }}
                          selected={false}
                        />
                      </Box>
                      <Button
                        fullWidth
                        disabled={!collectionCards?.length}
                        onClick={() => onNext('widgets')}
                      >
                        Next
                      </Button>
                    </>
                  )}
                  {activeStep === 2 && (
                    <>
                      <WidgetOptions
                        id={id}
                        pathname={location.pathname}
                        data={data}
                        onInputChange={onInputChange}
                        onRadioChange={onRadioChange}
                        onStepChange={onNext}
                      />
                      <Button fullWidth onClick={onSave}>
                        Save
                      </Button>
                    </>
                  )}
                </Box>
              </Paper>
            </Box>
          </Grid>
          <Grid item flex={1}>
            <Box className={classes.rightPanel}>
              <Box height="100%" mb={4}>
                <WidgetsPreview
                  pathname={location.pathname}
                  id={id}
                  data={data}
                  cards={collectionCards ?? []}
                  onSort={onSort}
                  onRemoveCard={onRemoveCard}
                  onUpdateConnectCard={onUpdateConnectCard}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Spin>
    </Wrapper>
  );
};

export default ConnectWidgetEditor;
